import { useCallback, useMemo } from 'react'
import ethIcon from 'ui/components/Icon/assets/eth.svg?url'
import solIcon from 'ui/components/Icon/assets/solana.svg?url'
import { notUndefined } from 'ui/utils/utils'

import { isTradingChain, tradingChains } from '../tradingConstants'
import { ChainWalletAssets, MergedTradingAsset } from '../tradingUtils'

export function useMergedAssets({ assets }: { assets?: ChainWalletAssets[] } = {}) {
    const mergeAssets = useCallback((assets: ChainWalletAssets[]) => {
        const flattenedAssets: MergedTradingAsset[] = assets
            .map((asset) => asset.tokens)
            .flat()
            .sort((a, b) => b.holdingValueCents - a.holdingValueCents)

        const base = assets.find((asset) => asset.chain === '8453')
        const baseTradingChain = tradingChains['8453']
        const baseNativeAsset = base
            ? ({
                  ...base.nativeAsset,
                  imageUrl: ethIcon,
                  decimals: baseTradingChain.decimals,
                  tokenAddress: baseTradingChain.nativeTokenAddress,
                  symbol: baseTradingChain.tokenSymbol,
                  name: baseTradingChain.name,
                  chain: baseTradingChain.chainId,
              } as const)
            : undefined

        const solana = assets.find((asset) => asset.chain === 'solana-mainnet')

        const solanaTradingChain = tradingChains['solana-mainnet']

        const solNativeAsset = solana
            ? ({
                  ...solana.nativeAsset,
                  imageUrl: solIcon,
                  decimals: solanaTradingChain.decimals,
                  tokenAddress: solanaTradingChain.nativeTokenAddress,
                  symbol: solanaTradingChain.tokenSymbol,
                  name: solanaTradingChain.name,
                  chain: solanaTradingChain.chainId,
              } as const)
            : undefined

        return [baseNativeAsset, solNativeAsset, ...flattenedAssets].filter(
            (asset): asset is MergedTradingAsset =>
                notUndefined(asset) && isTradingChain(asset.chain),
        )
    }, [])

    return {
        allAssets: useMemo(() => (assets ? mergeAssets(assets) : []), [mergeAssets, assets]),
        mergeAssets,
    }
}

export function isNativeAsset(asset: MergedTradingAsset | undefined) {
    if (!asset) {
        return false
    }

    return (
        asset.symbol === tradingChains['8453'].tokenSymbol ||
        asset.symbol === tradingChains['solana-mainnet'].tokenSymbol
    )
}
